import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { createComponent } from '@carvana/merch-components';
import React, { forwardRef, useContext, useRef, useState } from 'react';
import Slider from 'react-slick';
import styles from './Carousel.module.css';
import {
  CarouselArrowIcon,
  CarouselNavBtn,
  SkeletonTextMedium,
  SkeletonTextSmall
} from './Carousel.styles';
import { CarouselSettings } from './types';

export const CarouselWrapper = createComponent('div', {
  className: `${styles.wrapper}`
});

type CarouselProps = React.PropsWithChildren<CarouselSettings> & {
  viewLink?: string;
  children?: React.ReactNode;
};

export default forwardRef<HTMLElement, CarouselProps>(
  function Carousel(props, ref) {
    const {
      arrows = true,
      centerMode = false,
      className = '',
      dots = true,
      infinite = true,
      isSkeleton = false,
      heading,
      maxLength = 0,
      subHeading,
      slidesToShow = 4,
      slidesToScroll = 1,
      viewLink,
      children
    } = props;
    // Needed for swipe event tracking
    const { trackEvent } = useContext(AnalyticsContext);
    const [activeSlide, setActiveSlide] = useState(0);
    let sliderRef = useRef<Slider>(null);

    if (!maxLength) return null;

    function NavCarousel({ isLeft = false }: { isLeft?: boolean }) {
      const firstSlide = activeSlide === 0;
      const canRightNav = activeSlide + slidesToShow < maxLength;
      const outOfNavRange = (firstSlide && isLeft) || (!canRightNav && !isLeft);

      const handleClick = () => {
        if (isLeft) {
          sliderRef.current?.slickPrev();
        } else {
          sliderRef.current?.slickNext();
        }
      };

      return (
        <CarouselNavBtn
          type="button"
          isSkeleton={isSkeleton}
          disabled={outOfNavRange}
          data-analytics-id="Carousel nav"
          data-analytics-attributes={JSON.stringify({
            description: 'Carousel Nav',
            sectionName: heading,
            direction: isLeft ? 'left' : 'right'
          })}
          onClick={handleClick}
        >
          <CarouselArrowIcon isSkeleton={isSkeleton} isLeftArrow={isLeft} />
        </CarouselNavBtn>
      );
    }

    const sliderSettings = {
      arrows,
      afterChange: (current: number) => {
        if (activeSlide + slidesToShow - 1 <= maxLength) {
          sliderRef.current?.slickGoTo(current);
          setActiveSlide(current);
        } else {
          sliderRef.current?.slickGoTo(activeSlide);
        }
      },
      onSwipe: () => {
        trackEvent('Swiped - Home feed card', {
          description: 'Carousel Nav',
          sectionName: heading
        });
      },
      centerMode,
      className,
      dots,
      infinite,
      speed: 500,
      slidesToShow,
      slidesToScroll
    };

    return (
      <>
        <article
          className="mb-2xl flex w-full items-start justify-between sm:items-center"
          ref={ref}
        >
          <div>
            <h2 className="text-strong max-w-[552px] t-heading-2xl sm:t-heading-3xl md:max-w-full">
              {isSkeleton ? (
                <SkeletonTextMedium className=":w-[220px] w-[120px]" />
              ) : (
                heading
              )}
            </h2>
            {subHeading && (
              <h3>
                <small className="flex text-text-default t-body-lg">
                  <div className="mt-2xl m:mt-md">
                    {isSkeleton ? (
                      <SkeletonTextSmall className="w-[100px] w-[160px]" />
                    ) : (
                      subHeading
                    )}
                  </div>
                  <div className="hidden lg:flex">
                    &nbsp;
                    {viewLink && (
                      <a
                        className="underline"
                        href={viewLink}
                        data-analytics-id="View more results"
                      >
                        View More
                      </a>
                    )}
                  </div>
                </small>
              </h3>
            )}
          </div>
          <div className="text-icon-strong">
            <div className="hidden justify-between sm:flex sm:w-[100px] md:w-[112px]">
              <NavCarousel isLeft />
              <NavCarousel />
            </div>

            {viewLink && (
              <a
                className="md:hidden"
                href={viewLink}
                data-analytics-id="View more results"
              >
                View more
              </a>
            )}
          </div>
        </article>
        <CarouselWrapper>
          <Slider ref={sliderRef} {...sliderSettings}>
            {children}
          </Slider>
        </CarouselWrapper>
      </>
    );
  }
);
